// ============================================================================
// DuplicateBoundaries Component (View)
// ============================================================================

import { Box, Typography, Alert } from "@mui/material";
import { AffectedJobsPanel, ConfirmDeleteDialog, ConfirmMergeDialog } from "../../components";
import { MergeConflictModal } from "../../../../../shared/MergeConflictModal";
import JobMap from "../../../../../../helpers/JobMap";
import type { DuplicateBoundariesProps } from "./DuplicateBoundaries.types";
import { useDuplicateBoundariesViewModel } from "./DuplicateBoundaries.vm";
import "./DuplicateBoundaries.css";

/**
 * Component for displaying and resolving duplicate boundary issues
 */
export function DuplicateBoundaries(props: DuplicateBoundariesProps) {
	const vm = useDuplicateBoundariesViewModel(props);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="duplicate-boundaries no-issues">
				<Alert severity="success">
					<Typography>No duplicate boundaries detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue) {
		return null;
	}

	return (
		<Box className="duplicate-boundaries">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="body2" color="text.secondary">
					These jobs have identical boundaries. Merge or delete to resolve.
				</Typography>
			</Box>

			{/* Side-by-side layout: Content left, Map right */}
			<Box className="side-by-side-layout">
				{/* Left side - Job Legend */}
				<Box className="left-side">
					<AffectedJobsPanel
						jobs={vm.currentIssue.jobs}
						hiddenJobIds={vm.hiddenJobIds}
						onToggleVisibility={vm.toggleJobVisibility}
						onShowAll={vm.showAllJobs}
						onHideAll={vm.hideAllJobs}
						onDeleteJob={vm.handleDeleteJobClick}
						getJobSourceFiles={vm.getJobSourceFiles}
						onMerge={vm.handleMergeClick}
						canMerge={vm.canMerge}
					/>
				</Box>

				{/* Right side - Map */}
				<Box className="right-side">
					<JobMap
						jobs={vm.currentIssue.jobs}
						colorMode="boundary-outline"
						height="100%"
						hiddenJobIds={Array.from(vm.hiddenJobIds)}
					/>
				</Box>
			</Box>

			{/* Delete Confirmation Dialog */}
			<ConfirmDeleteDialog
				open={vm.deleteJobId !== null}
				title="Delete Job"
				message={`Are you sure you want to delete "${vm.getJobDisplayName(vm.deleteJobId || "")}"? This action cannot be undone.`}
				onConfirm={vm.handleConfirmDelete}
				onCancel={vm.handleCancelDelete}
			/>

			{/* Merge Confirmation Dialog */}
			<ConfirmMergeDialog
				open={vm.showMergeConfirm}
				jobCount={vm.currentIssue.jobs.length}
				onConfirm={vm.handleMergeConfirmDialog}
				onCancel={vm.handleMergeCancelDialog}
			/>

			{/* Merge Conflict Resolution Modal */}
			{vm.mergeConflicts && (
				<MergeConflictModal
					open={true}
					conflicts={vm.mergeConflicts}
					onConfirm={vm.handleMergeConfirm}
					onCancel={vm.handleMergeCancel}
					salespersons={props.salespersons}
					branches={props.branches}
					deals={props.deals}
				/>
			)}
		</Box>
	);
}
